import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Leaf, Apple, Dumbbell, Pill, Heart, ArrowRight, Sparkles, Check, Plane, Download } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/site/Nav";
import { NewsletterModal } from "@/components/site/NewsletterModal";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/site/Reveal";
import { ASSETS } from "@/data/site";
import { PLAN_CARDS } from "@/data/plans";
import { api, formatApiErrorDetail } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";

const CATEGORIES = [
  { icon: Sprout, title: "Detoxplan", desc: "Rens kroppen og heal deg selv frisk og sterk.", cta: "Les mer", to: "/plan/detox" },
  { icon: Leaf, title: "Livsstilsplan", desc: "Kosthold, livsstil og barn i én komplett pakke.", cta: "Les mer", to: "/plan/livsstil" },
  { icon: Dumbbell, title: "Booty & Hourglass", desc: "Treningsprogram og matplaner for sterk kropp.", cta: "Les mer", to: "/plan/booty" },
  { icon: Pill, title: "Anbefalte kosttilskudd", desc: "Nøye utvalgte anbefalinger – helt gratis.", cta: "Last ned gratis", to: "/plan/kosttilskudd" },
  { icon: Heart, title: "Om meg (Mine tips)", desc: "Mine beste tips, råd og erfaringer samlet her.", cta: "Les tips", href: "#tips" },
];

const PLANS = PLAN_CARDS;

export default function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [buying, setBuying] = useState(null);

  const handleBuy = async (lookup_key) => {
    setBuying(lookup_key);
    try { await startCheckout(lookup_key); }
    catch (e) {
      setBuying(null);
      toast.error(e?.response?.data?.detail || "Kunne ikke starte betaling. Prøv igjen.");
    }
  };

  const [consent, setConsent] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Du må samtykke til å motta nyhetsbrev."); return; }
    setSending(true);
    try {
      await api.post("/newsletter", { email, interest: "Nyhetsbrev", consent: true });
      toast.success("Takk! Du er påmeldt.");
      setEmail(""); setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="relative text-olive-deep overflow-clip" style={{ backgroundColor: "#F5EFE3", backgroundImage: `url(${ASSETS.paperTexture})`, backgroundSize: "640px" }}>
      <Nav />
      <Seo title="Helserebellen – Ta tilbake helsen på egne premisser" description="Konkrete, digitale planer for detox, livsstil, kosthold og trening – bygget på kunnskap og erfaring. Kunnskap. Frihet. Vitalitet." path="/" />
      <NewsletterModal />

      {/* ===================== HERO ===================== */}
      <section id="top" className="relative min-h-[100svh] pt-[74px] flex items-center overflow-hidden" data-testid="hero">
        {/* botanicals */}
        <img src={ASSETS.botanicalRight} alt="" className="pointer-events-none absolute -top-10 left-[26%] w-72 rotate-[8deg] blend-multiply opacity-90 hidden md:block" />
        <img src={ASSETS.botanicalLeft} alt="" className="pointer-events-none absolute top-16 -left-14 w-64 -rotate-12 blend-multiply opacity-80" />
        <img src={ASSETS.botanicalRight} alt="" className="pointer-events-none absolute top-8 right-4 w-72 rotate-[20deg] blend-multiply opacity-90 hidden lg:block" />
        <img src={ASSETS.botanicalLeft} alt="" className="pointer-events-none absolute bottom-4 right-10 w-72 rotate-[160deg] blend-multiply opacity-90 hidden md:block" />

        <div className="relative mx-auto max-w-[1400px] w-full px-6 md:px-10 grid lg:grid-cols-2 items-center gap-8">
          {/* big logo / hand */}
          <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="flex justify-center lg:justify-start">
            <img src={ASSETS.logo} alt="Helserebellen logo"
              className="w-[78%] max-w-[560px] object-contain mix-blend-multiply" data-testid="hero-logo" />
          </motion.div>

          {/* text */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className="text-center lg:text-left">
            <h1 className="font-serif font-medium text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-[-0.01em] text-olive-deep">
              Helserebellen
            </h1>
            <p className="mt-4 text-lg md:text-2xl font-bold uppercase tracking-[0.08em] text-terra">
              Kunnskap. Frihet. Vitalitet.
            </p>
            <p className="mt-6 text-lg md:text-xl text-olive/85 leading-relaxed max-w-md mx-auto lg:mx-0">
              For deg som vil ta tilbake helsen<br className="hidden sm:block" /> – på egne premisser.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
              <a href="#velg" data-testid="hero-cta-plans"
                className="inline-flex items-center justify-center rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors duration-300 shadow-sm">
                Utforsk planene
              </a>
              <Link to="/om-caroline" data-testid="hero-cta-about"
                className="inline-flex items-center justify-center rounded-md border border-olive/40 px-9 py-4 text-olive-deep text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep hover:text-cream transition-colors duration-300">
                Om Caroline
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===================== VELG DIN PLAN ===================== */}
      <section id="velg" className="relative py-20 md:py-24 px-5 md:px-10 bg-[#4A5138] text-cream overflow-hidden" data-testid="section-velg">
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal className="text-center">
            <h2 className="font-sans font-extrabold text-3xl md:text-4xl uppercase tracking-[0.06em]">Velg din plan</h2>
            <p className="mt-3 text-cream/70">Skreddersydde programmer som gir deg kunnskap, struktur og resultater.</p>
            <p className="mt-2 font-script text-2xl text-sage">Digitale programmer – du får tilgang med en gang!</p>
          </Reveal>

          <div className="relative mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07} className="h-full">
                <div data-testid={`plan-card-${i}`}
                  className="flex flex-col h-full rounded-xl bg-cream text-olive-deep overflow-hidden hover:-translate-y-1.5 transition-transform duration-400 shadow-[0_24px_50px_-28px_rgba(0,0,0,0.6)]">
                  <div className="h-48 overflow-hidden bg-cream-deep/40 flex items-center justify-center">
                    <img src={p.img} alt={p.title} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-sans font-bold text-lg text-olive-deep min-h-[3.25rem] flex items-start">{p.title}</h3>
                      {p.price && (
                        <span className={`font-serif text-lg ${p.free ? "rounded-full bg-sage/30 px-3 py-0.5 text-[12px] font-sans font-bold uppercase tracking-[0.1em] text-olive-deep" : "text-terra"}`}>
                          {p.price}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-[13px] text-olive/70 leading-relaxed flex-1">{p.desc}</p>
                    {p.comingSoon ? (
                      <span data-testid={`plan-cta-${i}`}
                        className="mt-5 self-start rounded-md bg-olive/10 px-6 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-olive/50">
                        Kommer snart
                      </span>
                    ) : p.free ? (
                      <div className="mt-5 flex items-center gap-2">
                        <button onClick={() => navigate(p.to)} data-testid={`plan-cta-${i}`}
                          className="rounded-md border border-olive/40 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-olive-deep hover:bg-olive-deep hover:text-cream transition-colors">
                          Les mer
                        </button>
                        <button onClick={() => navigate(p.to)} data-testid={`plan-buy-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-md bg-terra px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-cream hover:bg-olive-deep transition-colors">
                          <Download size={14} /> Last ned gratis
                        </button>
                      </div>
                    ) : (
                      <div className="mt-5 flex items-center gap-2">
                        <button onClick={() => navigate(p.to)} data-testid={`plan-cta-${i}`}
                          className="rounded-md border border-olive/40 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-olive-deep hover:bg-olive-deep hover:text-cream transition-colors">
                          Les mer
                        </button>
                        <button onClick={() => handleBuy(p.lookup_key)} disabled={buying === p.lookup_key} data-testid={`plan-buy-${i}`}
                          className="rounded-md bg-terra px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-cream hover:bg-olive-deep transition-colors disabled:opacity-60">
                          {buying === p.lookup_key ? "Sender…" : "Kjøp"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* ===== BUNDLE: Kjøp alle pakkene ===== */}
          <Reveal className="mt-8">
            <div data-testid="bundle-offer"
              className="relative overflow-hidden rounded-3xl bg-cream text-olive-deep p-7 md:p-10 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.7)] ring-1 ring-terra/25">
              {/* soft glow */}
              <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-terra/15 blur-3xl" />
              {/* rotated sticker */}
              <motion.div
                initial={{ rotate: -8, scale: 0.9 }}
                whileInView={{ rotate: -8, scale: 1 }}
                animate={{ y: [0, -5, 0] }}
                transition={{ y: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-10 grid place-items-center h-20 w-20 md:h-24 md:w-24 rounded-full bg-terra text-cream text-center shadow-lg">
                <div>
                  <span className="block font-script text-lg md:text-xl leading-none">Spar</span>
                  <span className="block font-sans font-extrabold text-base md:text-lg leading-tight">198,-</span>
                </div>
              </motion.div>

              <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-sage/25 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-olive-deep">
                    <Sparkles size={14} className="text-terra" /> Best verdi – hele pakken
                  </span>
                  <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">Kjøp <span className="text-terra">alle pakkene</span></h3>
                  <p className="mt-2 text-olive/70 text-[15px] max-w-md">Detox, Livsstil (inkl. barn) og Booty &amp; Hourglass – samlet i én komplett pakke. Alt du trenger for å ta tilbake helsen, på egne premisser.</p>
                  <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[14px]">
                    {["Detoxplan", "Livsstilsplan", "Booty & Hourglass"].map((t) => (
                      <li key={t} className="flex items-center gap-2 text-olive-deep">
                        <Check size={17} className="text-terra shrink-0" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="relative text-center lg:text-right lg:pr-28 pt-2">
                  <p className="text-olive/45 text-lg line-through decoration-terra/50">kr 1 197</p>
                  <p className="font-serif text-5xl md:text-6xl text-terra leading-none">kr 999</p>
                  <p className="mt-1 text-[12px] uppercase tracking-[0.14em] text-olive/50">Endelig pris · ingen MVA</p>
                  <button onClick={() => handleBuy("plan_bundle")} disabled={buying === "plan_bundle"} data-testid="bundle-buy"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-olive-deep px-8 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.1em] hover:bg-terra transition-colors disabled:opacity-60">
                    {buying === "plan_bundle" ? "Sender…" : "Kjøp alle pakkene"}
                    <ArrowRight size={17} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== OM CAROLINE ===================== */}
      <section id="om" className="relative py-16 md:py-24 px-5 md:px-10 overflow-hidden" data-testid="section-om">
        <img src={ASSETS.botanicalRight} alt="" className="pointer-events-none absolute top-10 right-0 w-72 rotate-[15deg] blend-multiply opacity-90 hidden lg:block" />
        <div className="mx-auto max-w-[1200px] grid lg:grid-cols-[1fr_1.8fr] gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="relative mx-auto lg:mx-0 max-w-[300px] w-full rounded-2xl overflow-hidden aspect-[3/4] shadow-xl">
              <img src={ASSETS.caroline} alt="Caroline" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="relative">
            <Reveal>
              <p className="font-script text-4xl md:text-5xl text-terra">Hei, jeg heter Caroline.</p>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-6 space-y-4 text-olive/85 leading-relaxed">
                <p>Jeg brenner for én ting: å hjelpe deg forstå kroppen din, ta tilbake kontrollen over egen helse og kjenne hvor bra du faktisk kan ha det.</p>
                <p>Gjennom snart 20 år i helse- og ernæringsbransjen har jeg sett trendene komme og gå. Nye dietter. Nye mirakelkurer. Nye råd. Samtidig blir mennesker mer slitne, mer forvirret og stadig mer opptatt av helse. Jeg har testet ALT! Og med ekte erfaring og årevis med research ønsket jeg å starte Helserebellen.</p>
                <p>Jeg mener helse ikke trenger å være så komplisert som vi har gjort det til. Vi blir lurt til å tro at kroppen vår er langt mindre fantastisk enn den egentlig er, og at vi er avhengige av medisin for å nærmest leve eller overleve.</p>
                <p>Jeg tror på ren mat, bevegelse, søvn, dagslys, natur, gode rutiner – og på å forstå kroppen fremfor å kjempe mot den.</p>
                <p>Her får du konkrete planer bygget på kunnskap, erfaring og det jeg mener faktisk fungerer i praksis.</p>
                <p className="font-bold text-olive-deep">Jeg tør å bevege meg ut på sykdomsterritorium, og komme med konkrete forslag til hvordan du kan heale diverse alvorlige sykdommer.</p>
                <p>Jeg gir deg verktøyene du trenger for å ta bedre valg for deg selv og familien din.</p>
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-7 font-script text-2xl md:text-3xl text-terra leading-snug">
                Du trenger ikke vite alt.<br />
                Du trenger en plan.<br />
                Jeg har laget den for deg.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-olive/85">Vil du vite hvem jeg er og hvorfor jeg startet Helserebellen?</p>
              <Link to="/om-caroline" data-testid="om-cta"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-terra px-8 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors duration-300">
                Les mer om meg →
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== PLANER OG KATEGORIER ===================== */}
      <section id="planer" className="py-20 md:py-24 px-5 md:px-10" data-testid="section-planer">
        <div className="mx-auto max-w-[1400px]">
          <Reveal className="text-center">
            <h2 className="font-sans font-extrabold text-2xl md:text-3xl uppercase tracking-[0.08em] text-olive-deep">Planer og kategorier</h2>
            <div className="mx-auto mt-4 h-[3px] w-16 bg-terra rounded-full" />
          </Reveal>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {CATEGORIES.map((c, i) => {
              const cls = "group flex flex-col items-center text-center h-full rounded-xl border border-olive/12 bg-cream-deep/30 px-4 py-8 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_-24px_rgba(86,94,66,0.6)] hover:border-terra/40 transition-all duration-400";
              const inner = (
                <>
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sage/25 text-olive-deep group-hover:bg-terra group-hover:text-cream transition-colors duration-300">
                    <c.icon size={30} strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-5 font-sans font-bold text-sm uppercase tracking-[0.04em] text-olive-deep leading-tight">{c.title}</h3>
                  <p className="mt-3 text-[13px] text-olive/70 leading-relaxed flex-1">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-terra">
                    {c.cta} <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </>
              );
              return (
                <Reveal key={c.title} delay={i * 0.05}>
                  {c.to ? (
                    <Link to={c.to} data-testid={`category-${i}`} className={cls}>{inner}</Link>
                  ) : (
                    <a href={c.href} data-testid={`category-${i}`} className={cls}>{inner}</a>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== KOSTTILSKUDD + MINE TIPS ===================== */}
      <section className="py-16 md:py-20 px-5 md:px-10">
        <div className="mx-auto max-w-[1400px] grid md:grid-cols-2 gap-6">
          <Reveal>
            <div id="kosttilskudd" data-testid="section-kosttilskudd"
              className="flex flex-col sm:flex-row items-stretch h-full rounded-xl border border-olive/12 bg-cream-deep/30 overflow-hidden">
              <div className="sm:w-2/5 h-48 sm:h-auto overflow-hidden">
                <img src={ASSETS.supplements} alt="Kosttilskudd" className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col p-7 sm:w-3/5">
                <h3 className="font-serif text-2xl text-olive-deep leading-tight">Kosttilskudd<br /><span className="text-terra text-lg">– anbefalinger jeg stoler på</span></h3>
                <p className="mt-3 text-[14px] text-olive/75 leading-relaxed flex-1">Jeg tester og velger kun det jeg selv bruker og tror på. Kvalitet, rene ingredienser og dokumentert effekt.</p>
                <Link to="/plan/kosttilskudd" data-testid="kosttilskudd-cta"
                  className="mt-5 self-start inline-flex items-center gap-1.5 rounded-md bg-terra px-6 py-2.5 text-cream text-[11px] font-semibold uppercase tracking-[0.1em] hover:bg-olive-deep transition-colors">
                  <Download size={13} /> Last ned gratis
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div id="tips" data-testid="section-tips"
              className="flex flex-col sm:flex-row items-stretch h-full rounded-xl border border-olive/12 bg-cream-deep/30 overflow-hidden">
              <div className="sm:w-2/5 h-48 sm:h-auto overflow-hidden">
                <img src={ASSETS.tips} alt="Mine tips" className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col p-7 sm:w-3/5">
                <h3 className="font-serif text-2xl text-olive-deep leading-tight">Om meg (Mine tips)<br /><span className="text-terra text-lg">Kunnskap du kan bruke</span></h3>
                <p className="mt-3 text-[14px] text-olive/75 leading-relaxed flex-1">Her deler jeg mine beste tips, råd og erfaringer innen helse, kosthold, livsstil, hormoner, energi og mye mer.</p>
                <button onClick={() => navigate("/register")} data-testid="tips-cta"
                  className="mt-5 self-start rounded-md bg-olive-deep px-6 py-2.5 text-cream text-[11px] font-semibold uppercase tracking-[0.1em] hover:bg-terra transition-colors">
                  Bla gjennom tipsene
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== RETREAT I SPANIA (teaser) ===================== */}
      <section className="relative overflow-hidden" data-testid="section-retreat">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_nordic-wellness-2/artifacts/4nfo89zl_videoretreat.mp4"
          autoPlay muted loop playsInline preload="metadata" aria-hidden="true" />
        {/* terracotta wash for readability + brand feel */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#6d3326]/90 via-[#8a4231]/60 to-[#8a4231]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-olive-deep/70 via-transparent to-transparent" />

        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-36 min-h-[560px] flex items-center">
          <Reveal className="max-w-xl text-cream">
            <span className="inline-flex items-center gap-2 rounded-full bg-cream/15 backdrop-blur-sm px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]">
              <Plane size={15} strokeWidth={1.9} /> Retreats · Spania
            </span>
            <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[1.05]">
              Bli med på retreat<br /><span className="text-terra-soft">under spansk sol</span>
            </h2>
            <p className="mt-5 text-cream/85 text-[15px] md:text-lg leading-relaxed max-w-md">
              Noen få dager der du kobler av, spiser ekte mat, beveger deg i naturen og fyller på med energi – sammen med likesinnede. Kom hjem som en ny versjon av deg selv.
            </p>
            <a href="https://retreat-booking-8.emergent.host/?utm_source=share" target="_blank" rel="noopener noreferrer"
              data-testid="retreat-cta"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.1em] hover:bg-cream hover:text-olive-deep transition-colors">
              <Plane size={17} strokeWidth={1.9} /> Utforsk retreats i Spania
              <ArrowRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ===================== FOOTER SLOGAN BAR ===================== */}
      <div className="bg-terra text-cream text-center py-7 px-5" data-testid="footer-slogan">
        <p className="mx-auto max-w-2xl text-center text-sm md:text-base leading-relaxed">
          <Heart size={17} className="inline-block align-[-3px] mr-2 fill-cream/90" /> Du fortjener en kropp som fungerer. Et liv du elsker. En hverdag med energi og frihet.
        </p>
        <p className="mt-1 font-script text-2xl text-cream/90">La oss gjøre det – sammen.</p>
      </div>

      {/* slim functional footer (nyhetsbrev + logg inn) */}
      <footer className="bg-olive-deep text-cream/90 py-8 px-5 md:px-10" data-testid="footer">
        <div className="mx-auto max-w-[1400px] flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <span className="block font-sans font-extrabold uppercase text-sm">Helserebellen</span>
              <span className="block text-[9px] tracking-[0.16em] text-cream/50 uppercase mt-1">© {new Date().getFullYear()}</span>
            </div>
          </div>
          <form onSubmit={subscribe} className="flex flex-col gap-2 w-full md:w-auto" data-testid="newsletter-form">
            <p className="text-[11px] text-cream/60 md:text-right">Få tips og nyheter på e-post. Ingen spam – meld deg av når som helst.</p>
            <div className="flex items-center gap-2">
              <input required type="email" placeholder="Din e-post" value={email} data-testid="newsletter-email"
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 md:w-64 rounded-md bg-cream/10 border border-cream/20 px-4 py-2.5 text-sm placeholder:text-cream/40 focus:outline-none focus:border-terra" />
              <button type="submit" disabled={sending} data-testid="newsletter-submit"
                className="rounded-md bg-terra px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] hover:bg-cream hover:text-olive-deep transition-colors disabled:opacity-60">
                {sending ? "…" : "Meld på"}
              </button>
            </div>
            <label className="flex items-start gap-2 text-[11px] text-cream/70 max-w-xs md:max-w-sm cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                data-testid="newsletter-consent" className="mt-0.5 accent-terra" />
              <span>Jeg samtykker til å motta nyhetsbrev og har lest{" "}
                <Link to="/personvern" className="underline hover:text-terra">personvernerklæringen</Link>.</span>
            </label>
          </form>
        </div>

        <div className="mx-auto max-w-[1400px] mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-cream/60" data-testid="footer-legal-links">
          <Link to="/personvern" className="hover:text-terra">Personvern</Link>
          <Link to="/vilkar" className="hover:text-terra">Kjøpsvilkår</Link>
          <Link to="/avmelding" className="hover:text-terra">Meld av nyhetsbrev</Link>
        </div>

        <div className="mx-auto max-w-[1400px] mt-8 pt-6 border-t border-cream/15" data-testid="footer-disclaimer">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cream/50 mb-2">Ansvarsfraskrivelse</p>
          <p className="text-[12px] leading-relaxed text-cream/55 max-w-4xl">
            Innholdet på Helserebellen og i planene er basert på mine personlige erfaringer, meninger og research, og er
            kun ment som generell informasjon og inspirasjon. Det er ikke medisinske råd og erstatter ikke konsultasjon,
            diagnose eller behandling fra lege eller annet kvalifisert helsepersonell. Alle rådene er personlige
            anbefalinger – ikke løfter om resultater. Mennesker er forskjellige, og jeg kan ikke garantere at du blir
            frisk eller oppnår bestemte resultater. Du er selv ansvarlig for egne valg og for hvordan du bruker
            informasjonen. Rådfør deg alltid med lege før du gjør endringer i kosthold, livsstil, trening eller
            medisinering – spesielt ved sykdom, graviditet, amming eller bruk av medisiner. Helserebellen v/Caroline
            fraskriver seg ethvert ansvar for skader, tap eller konsekvenser som måtte følge av bruk av innholdet.
          </p>
        </div>
      </footer>
    </div>
  );
}
